import { Helmet } from "react-helmet-async";

interface TableRow {
  [key: string]: string | number;
}

interface TableSchemaProps {
  name: string;
  description?: string;
  columns: string[];
  rows: TableRow[];
  about?: string;
}

/**
 * Table schema for structured data tables (e.g., cost comparisons).
 * Helps search engines understand tabular data and display rich snippets.
 * 
 * @see https://schema.org/Table
 */
export function TableSchema({
  name,
  description,
  columns,
  rows,
  about,
}: TableSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Table",
    name,
    ...(description && { description }),
    ...(about && { about }),
    // Represent table structure
    tableContent: {
      "@type": "ItemList",
      itemListElement: [
        // Header row
        {
          "@type": "ListItem",
          position: 0,
          item: {
            "@type": "TableRow",
            rowContent: columns.map((col, idx) => ({
              "@type": "TableCell",
              position: idx,
              text: col,
            })),
          },
        },
        // Data rows
        ...rows.map((row, rowIdx) => ({
          "@type": "ListItem",
          position: rowIdx + 1,
          item: {
            "@type": "TableRow",
            rowContent: columns.map((col, colIdx) => ({
              "@type": "TableCell",
              position: colIdx,
              text: String(row[col] || ""),
            })),
          },
        })),
      ],
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
